import React from 'react';
import { Link } from 'react-router-dom';
import { Pill, InlineDropDown } from '@ems/client-design-system';
// shared components
import { TableIcon, ColumnHeader, TooltipDropdown } from 'src/components';
// action
import { sortTable } from 'src/@operations/actions';
// utils
import { getDeployedProductId, tableDateTimeFormat, tableDateTimeFormatShort } from 'src/utils';
// const
import { CORRELATED_TYPES, OPERATIONS, NULL_VALUE } from 'src/constants';

const formatIconField = (
  id: number,
  operation: any,
  fieldIcon: JSX.Element,
  fieldTooltip: string,
  iconPrefix: string,
  fieldType: string,
  translationDataList: any,
  popoverData: any,
  onUpdateSelection: any,
  reduceChevronPadding: boolean,
  canUpdate: boolean
) => {
  const updateSelection = selection => {
    onUpdateSelection(id, { [fieldType]: selection.key });
  };

  const icon = (type, size) =>
    TableIcon({
      name: type.toLowerCase(),
      prefix: iconPrefix,
      size,
    });

  const selectedItem = {
    key: `${operation.tableId}-${fieldType}`,
    label: '',
    icon: fieldIcon,
  };

  if (!canUpdate) {
    return <div className="u-flex-center">{fieldIcon}</div>;
  }

  const fieldOptions: any[] = [];
  const options = popoverData;
  options.map((key: string) => {
    const option = {
      key,
      label: translationDataList[key],
      icon: icon(key.toLowerCase(), 20),
    };
    fieldOptions.push(option);
  });

  return (
    <div className="u-flex-center">
      <TooltipDropdown
        text={translationDataList[fieldTooltip]}
        searchItems={fieldOptions}
        updateSelection={updateSelection}
        selectedItem={selectedItem}
        isIconOnly={reduceChevronPadding}
      />
    </div>
  );
};

const correlatedPill = (correlatedItems, tableId, translation) => {
  const displayItems: any[] = [];
  correlatedItems.sort((a, b) => {
    if (a.type < b.type) {
      return -1;
    } else if (a.type > b.type) {
      return 1;
    }

    const dataA = translation[a.data];
    const dataB = translation[b.data];
    if (dataA < dataB) {
      return -1;
    } else if (dataA > dataB) {
      return 1;
    }

    if (a.id < b.id) {
      return -1;
    } else if (a.id > b.id) {
      return 1;
    }

    return 0;
  });

  correlatedItems.map(item => {
    if (item.type === CORRELATED_TYPES.INFRINGEMENT) {
      const type = item.data;
      const translatedTitle = translation[type]; // example: type = "Cco"
      displayItems.push(
        <div
          key={`${tableId}_${translatedTitle}_${item.id}`}
          className={`operation_pill ${type.length <= 3 && 'operation_pill--uppercase'}`}>
          <Link
            to={{
              pathname: `/${getDeployedProductId()}/infringements/${item.id}`,
            }}
            className="pill--link"
            tabIndex={0}>
            <Pill title={translatedTitle} icon="ic-infringements" type={item.type} />
          </Link>
        </div>
      );
    }

    if (item.type === CORRELATED_TYPES.NOISEEVENT) {
      const type = item.data;
      const translatedTitle = translation[type]; // example: type = "Cco"

      displayItems.push(
        <div key={`${tableId}_${translatedTitle}_${item.id}`} className={`operation_pill`}>
          <Link
            to={{
              pathname: `/${getDeployedProductId()}/noiseevents/${item.id}`,
            }}
            className="pill--link"
            tabIndex={0}>
            <Pill title={translatedTitle} icon="ic-noise" type={CORRELATED_TYPES.NOISEEVENT} />
          </Link>
        </div>
      );
    }
  });

  if (displayItems.length > 0) {
    return displayItems;
  } else {
    return '';
  }
};

const formatRunway = (
  id: number,
  operation: any,
  runwayName: string | null,
  translationData: any,
  popoverData: any,
  onUpdateSelection: any,
  canUpdate: boolean
) => {
  const label = runwayName ? runwayName : '-';
  if (!canUpdate) {
    return <div className="u-flex-center">{label}</div>;
  }

  const updateSelection = selection => {
    const key = selection.key === NULL_VALUE ? null : selection.key;
    onUpdateSelection(id, { runwayName: key });
  };

  const runwayNameOptions: any[] = [];
  const options = popoverData;
  options.map((key: string) => {
    const NullValue = { key: NULL_VALUE, label: translationData.NullValue };

    const option = {
      key,
      label: key,
    };

    if (key === NULL_VALUE) {
      runwayNameOptions.push(NullValue);
    } else {
      runwayNameOptions.push(option);
    }
  });

  const selectedItem = {
    key: `${operation.tableId}-runway`,
    label,
  };

  return (
    <div className="u-flex-center">
      <InlineDropDown
        searchItems={runwayNameOptions}
        updateSelection={updateSelection}
        selectedItem={selectedItem}
        isIconOnly={false}
        hasIcon={false}
        isNullable={false}
      />
    </div>
  );
};

const formatLeftField = (
  id: number,
  operation: any,
  modelName: string,
  fieldType: string,
  translationData: any,
  popoverData: any,
  onUpdateSelection: any,
  canUpdate: boolean
) => {
  const label = modelName ? modelName : '—';
  if (!canUpdate) {
    return label;
  }

  const updateSelection = selection => {
    const key = selection.key === NULL_VALUE ? null : selection.key;
    onUpdateSelection(id, { [fieldType]: key });
  };

  const fieldOptions: any[] = [];
  const options = popoverData;
  options.map((key: string) => {
    const NullValue = { key: NULL_VALUE, label: translationData.NullValue };

    const option = {
      key,
      label: key,
    };

    if (key === NULL_VALUE) {
      fieldOptions.push(NullValue);
    } else {
      fieldOptions.push(option);
    }
  });

  const selectedItem = {
    key: `${operation.tableId}-${fieldType}`,
    label,
  };

  return (
    <InlineDropDown
      searchItems={fieldOptions}
      updateSelection={updateSelection}
      selectedItem={selectedItem}
      isIconOnly={false}
      hasIcon={false}
      isNullable={false}
    />
  );
};

/*
 * Rule Name link
 */
const linkToOpDetails = (rule: string | null, id: any | null, ruleInfo) => (
  <Link
    className="rule_link"
    to={{
      pathname: `/${getDeployedProductId()}/${OPERATIONS}/${id}`,
      search: undefined,
      state: ruleInfo,
    }}>
    {rule}
  </Link>
);

const formatADIcon = (adFlag: string = '') => {
  return TableIcon({
    name: `${adFlag}`.toLowerCase(),
    prefix: 'ad',
    size: 32,
  });
};

const formatFlightIcon = (acType: string | null) => {
  if (!acType) {
    return TableIcon({
      name: 'unknown',
      prefix: 'ac',
      size: 24,
    });
  }

  return TableIcon({
    name: acType.toLowerCase(),
    prefix: 'ac',
    size: 24,
  });
};

const formatCorrelated = (correlatedData, appliedFilters) => {
  const correlatedItems: object[] = [];

  if (correlatedData.infringements) {
    for (const inf of correlatedData.infringements) {
      if (
        !appliedFilters.length ||
        appliedFilters.findIndex(f => f.key === 'Infringements') !== -1 ||
        appliedFilters.findIndex(
          f => f.key.toLowerCase() === inf.infringementType.toLowerCase()
        ) !== -1
      ) {
        correlatedItems.push({
          type: CORRELATED_TYPES.INFRINGEMENT,
          data: inf.infringementType,
          id: inf.id,
        });
      }
    }
  }

  if (correlatedData.noiseEvents) {
    for (const inf of correlatedData.noiseEvents) {
      if (!appliedFilters.length || appliedFilters.findIndex(f => f.key === 'NoiseEvents') !== -1) {
        correlatedItems.push({
          type: CORRELATED_TYPES.NOISEEVENT,
          data: 'NoiseEvents',
          id: inf.id,
        });
      }
    }
  }

  return correlatedItems;
};

export const formatRunwayField = (runway: string | null) => (runway ? runway : '—');

export const addDisplayField = (operation, appliedFilters) => ({
  time: tableDateTimeFormat(operation.time),
  'time-short': tableDateTimeFormatShort(operation.time),
  adIcon: formatADIcon(operation.operationType),
  adType: operation.operationType,
  flightCategory: operation.aircraftCategory,
  flightIcon: formatFlightIcon(operation.aircraftCategory),
  runway: formatRunwayField(operation.runwayName),
  correlated: formatCorrelated(operation.correlated, appliedFilters.correlated),
  model: operation.aircraftType,
});

/*
 * Prepare operation for render on table
 */
export const formatOperationsData = (
  data: any,
  translationData: any,
  filterData: any,
  onUpdateSelection: any,
  ruleInfo: any,
  appliedFilters: any,
  canUpdate: boolean
) => {
  const mapObject = new Map();
  for (const [id, object] of data) {
    const operation = Object.assign({}, object);
    const displayFields = addDisplayField(operation, appliedFilters);
    operation.acid = linkToOpDetails(operation.acid, operation.id, ruleInfo);
    operation.displayFlag = formatIconField(
      id,
      operation,
      displayFields.adIcon,
      displayFields.adType,
      'ad',
      'operationType',
      translationData,
      filterData.operationTypes,
      onUpdateSelection,
      true,
      canUpdate
    );
    operation.displayCategory = formatIconField(
      id,
      operation,
      displayFields.flightIcon,
      displayFields.flightCategory,
      'ac',
      'aircraftCategory',
      translationData,
      filterData.aircraftCategories,
      onUpdateSelection,
      false,
      canUpdate
    );
    operation.displayRunwayName = formatRunway(
      id,
      operation,
      displayFields.runway,
      translationData,
      filterData.runwayNames,
      onUpdateSelection,
      canUpdate
    );
    operation.correlated = correlatedPill(
      displayFields.correlated,
      operation.tableId,
      translationData
    );
    operation.aircraftType = formatLeftField(
      id,
      operation,
      displayFields.model,
      'aircraftType',
      translationData,
      filterData.aircraftTypes,
      onUpdateSelection,
      canUpdate
    );
    operation.airportId = formatLeftField(
      id,
      operation,
      operation.airportId,
      'airportId',
      translationData,
      filterData.airportIds,
      onUpdateSelection,
      canUpdate
    );
    operation.remoteAirportId = formatLeftField(
      id,
      operation,
      operation.remoteAirportId,
      'remoteAirportId',
      translationData,
      filterData.remoteAirportIds,
      onUpdateSelection,
      canUpdate
    );
    operation.displayTime = displayFields.time;
    operation[`displayTime-short`] = displayFields[`time-short`];
    mapObject.set(id, operation);
  }
  return mapObject;
};

/*
 * Prepare operation header
 */
export const formatHeaders = (
  resultSize: number,
  isLoading: boolean,
  dispatcher: any,
  sortSelectors: any,
  translationData: any
) => {
  // Overrides the headers provided
  // could improve and loop through data from config

  const {
    fields: { operations: rowHeaders },
    abbreviations: { aircraftCategory: aircraftCategoryAbrr, runwayName: runwayNameAbbr },
    components: {
      labels: { sortBy },
    },
  } = translationData;

  const acid = ColumnHeader({
    sortName: 'acid',
    resultSize,
    isLoading,
    dispatcher,
    sortSelectors,
    sortTable,
    languageData: {
      title: rowHeaders.acid,
      abbreviation: null,
      sortBy,
    },
  });
  const displayFlag = ColumnHeader({
    titleIcon: TableIcon({
      name: 'arrival',
      prefix: 'ad',
      size: 24,
      className: isLoading ? 'disabled' : '',
    }),
    sortName: 'operationType',
    resultSize,
    isLoading,
    dispatcher,
    sortSelectors,
    sortTable,
    languageData: {
      title: rowHeaders.operationType,
      abbreviation: null,
      sortBy,
    },
  });
  const displayTime = ColumnHeader({
    sortName: 'time',
    resultSize,
    isLoading,
    dispatcher,
    sortSelectors,
    sortTable,
    languageData: {
      title: rowHeaders.displayTime,
      abbreviation: null,
      sortBy,
    },
  });
  const airportId = ColumnHeader({
    sortName: 'airportId',
    resultSize,
    isLoading,
    dispatcher,
    sortSelectors,
    sortTable,
    languageData: {
      title: rowHeaders.airportId,
      abbreviation: null,
      sortBy,
    },
  });
  const remoteAirportId = ColumnHeader({
    sortName: 'remoteAirportId',
    resultSize,
    isLoading,
    dispatcher,
    sortSelectors,
    sortTable,
    languageData: {
      title: rowHeaders.remoteAirportId,
      abbreviation: null,
      sortBy,
    },
  });
  const displayRunwayName = ColumnHeader({
    sortName: 'runwayName',
    resultSize,
    isLoading,
    dispatcher,
    sortSelectors,
    sortTable,
    languageData: {
      title: rowHeaders.displayRunwayName,
      abbreviation: runwayNameAbbr,
      sortBy,
    },
  });
  const displayCategory = ColumnHeader({
    sortName: 'aircraftCategory',
    resultSize,
    isLoading,
    dispatcher,
    sortSelectors,
    sortTable,
    languageData: {
      title: rowHeaders.displayCategory,
      abbreviation: aircraftCategoryAbrr,
      sortBy,
    },
  });
  const aircraftType = ColumnHeader({
    sortName: 'aircraftType',
    resultSize,
    isLoading,
    dispatcher,
    sortSelectors,
    sortTable,
    languageData: {
      title: rowHeaders.aircraftType,
      abbreviation: null,
      sortBy,
    },
  });
  const correlated = ColumnHeader({
    sortName: 'correlated',
    resultSize,
    isLoading,
    dispatcher,
    sortSelectors,
    sortable: false,
    sortTable,
    languageData: {
      title: rowHeaders.correlated,
      abbreviation: null,
      sortBy,
    },
  });

  return Object.assign({}, rowHeaders, {
    acid,
    displayFlag,
    displayTime,
    airportId,
    remoteAirportId,
    displayRunwayName,
    displayCategory,
    aircraftType,
    correlated,
  });
};
